import { IntentProps, useFullTheme } from '~/design';
import { SvgIconProps, SvgMultivariantIconProps } from '@applyboard/ui-icons';
import { svgVerticalAlign } from '~/utils/styling';

export interface IconWrapperProps extends Required<IntentProps> {
  /** If true, the icon will change colour when hovered/pressed (or rather, when its parent element
   * is hovered/pressed.) (default: true) */
  allowInteractions?: boolean;

  /** The icon to render. */
  children: React.ReactElement<SvgIconProps | SvgMultivariantIconProps>;

  /** The contrast of the icon against the theme's default background. */
  contrast?: 'high' | 'mid' | 'low' | 'inverted';

  /** Whether or not the icon should render as "inactive". This should be used when the icon's
   * parent component can become disabled. */
  disabled?: boolean;

  /** Saliency sets how the icon default state will stand out from the background. */
  saliency?: 'default' | 'inverted';

  /** A CSS selector that identifies the icon's parent element. For example, if the icon is within
   * a button, the selector could simply be `button`. */
  selector: string;
}

export interface IconWrapperForButtonProps {
  contrast: IconWrapperProps['contrast'];
  disabled: IconWrapperProps['disabled'];
  intent: IconWrapperProps['intent'];
  saliency?: IconWrapperProps['saliency'];
  selector: 'a' | 'button';
}

type ColorState = {
  default: string;
  hover: string;
  active: string;
};

type ColorObject = {
  [key: string]: ColorState;
};

/**
 * This component wraps around an icon (from @applyboard/ui-icons) and applies styling to it for
 * "hover" and "active" states. The icon should be part of an interactive component, such as:
 * button; select; chip; accordion; etc. When that component is interacted with, the icon's color
 * will change.
 */
export function IconWrapper({
  allowInteractions = true,
  contrast,
  children,
  disabled,
  intent,
  saliency = 'default',
  selector,
}: IconWrapperProps) {
  const { colors } = useFullTheme();

  const regularColors = {
    primary: {
      default: colors.iconPrimary,
      hover: colors.iconPrimaryHover,
      active: colors.iconPrimaryPressed,
    },
    secondary: {
      default: colors.iconSecondary,
      hover: colors.iconSecondaryHover,
      active: colors.iconSecondaryPressed,
    },
    positive: {
      default: colors.iconPositive,
      hover: colors.iconPositiveHover,
      active: colors.iconPositivePressed,
    },
    negative: {
      default: colors.iconNegative,
      hover: colors.iconNegativeHover,
      active: colors.iconNegativePressed,
    },
    magic: {
      default: colors.iconMagic,
      hover: colors.iconMagicHover,
      active: colors.iconMagicPressed,
    },
    warning: {
      default: colors.iconWarning,
      hover: colors.iconWarningHover,
      active: colors.iconWarningPressed,
    },
  };

  const highContrastColors = {
    primary: {
      default: colors.iconPrimaryHighContrast,
      hover: colors.iconPrimaryHighContrastHover,
      active: colors.iconPrimaryHighContrastPressed,
    },
    secondary: {
      default: colors.iconSecondaryHighContrast,
      hover: colors.iconSecondaryHighContrastHover,
      active: colors.iconSecondaryHighContrastPressed,
    },
    positive: {
      default: colors.iconPositiveHighContrast,
      hover: colors.iconPositiveHighContrastHover,
      active: colors.iconPositiveHighContrastPressed,
    },
    negative: {
      default: colors.iconNegativeHighContrast,
      hover: colors.iconNegativeHighContrastHover,
      active: colors.iconNegativeHighContrastPressed,
    },
    magic: {
      default: colors.iconMagicHighContrast,
      hover: colors.iconMagicHighContrastHover,
      active: colors.iconMagicHighContrastPressed,
    },
    warning: {
      default: colors.iconWarningHighContrast,
      hover: colors.iconWarningHighContrastHover,
      active: colors.iconWarningHighContrastPressed,
    },
  };

  const lowContrastColors = {
    primary: {
      default: colors.iconPrimaryLowContrast,
      hover: colors.iconPrimaryLowContrastHover,
      active: colors.iconPrimaryLowContrastPressed,
    },
    secondary: {
      default: colors.iconSecondaryLowContrast,
      hover: colors.iconSecondaryLowContrastHover,
      active: colors.iconSecondaryLowContrastPressed,
    },
    positive: {
      default: colors.iconPositiveLowContrast,
      hover: colors.iconPositiveLowContrastHover,
      active: colors.iconPositiveLowContrastPressed,
    },
    negative: {
      default: colors.iconNegativeLowContrast,
      hover: colors.iconNegativeLowContrastHover,
      active: colors.iconNegativeLowContrastPressed,
    },
    magic: {
      default: colors.iconMagicLowContrast,
      hover: colors.iconMagicLowContrastHover,
      active: colors.iconMagicLowContrastPressed,
    },
    warning: {
      default: colors.iconWarningLowContrast,
      hover: colors.iconWarningLowContrastHover,
      active: colors.iconWarningLowContrastPressed,
    },
  };

  const invertedColors = {
    primary: {
      default: colors.iconInverted,
      hover: colors.iconInvertedHover,
      active: colors.iconInvertedPressed,
    },
    secondary: {
      default: colors.iconInverted,
      hover: colors.iconInvertedHover,
      active: colors.iconInvertedPressed,
    },
    positive: {
      default: colors.iconInverted,
      hover: colors.iconInvertedHover,
      active: colors.iconInvertedPressed,
    },
    negative: {
      default: colors.iconInverted,
      hover: colors.iconInvertedHover,
      active: colors.iconInvertedPressed,
    },
    magic: {
      default: colors.iconInverted,
      hover: colors.iconInvertedHover,
      active: colors.iconInvertedPressed,
    },
    warning: {
      default: colors.iconInverted,
      hover: colors.iconInvertedHover,
      active: colors.iconInvertedPressed,
    },
  };

  const _colorsObject =
    contrast === 'high'
      ? highContrastColors
      : contrast === 'mid'
        ? regularColors
        : contrast === 'low'
          ? lowContrastColors
          : contrast === 'inverted'
            ? invertedColors
            : regularColors;

  const colorsObject =
    saliency === 'default'
      ? _colorsObject
      : Object.entries(_colorsObject).reduce((adjustedColors: ColorObject, [key, value]) => {
          adjustedColors[key] = { ...value, default: colors.iconInverted };
          return adjustedColors;
        }, {});

  return (
    <span
      css={[
        {
          flexShrink: 0,
          lineHeight: 0,
          [`${selector} &`]: {
            color: disabled ? colors.iconInactive : colorsObject[intent].default,
          },
        },
        allowInteractions && {
          [`${selector}:hover &`]: {
            color: disabled ? colors.iconInactive : colorsObject[intent].hover,
          },
          [`${selector}:active &`]: {
            color: disabled ? colors.iconInactive : colorsObject[intent].active,
          },
        },
        svgVerticalAlign,
      ]}
      data-icon-color={intent}
      data-icon-contrast={contrast}
    >
      {children}
    </span>
  );
}
